import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { motion, AnimatePresence } from "framer-motion";
import "react-loading-skeleton/dist/skeleton.css";

import Pagination from "../../../components/Common/Pagination";
import ProjectCards from "./ProjectCards";
import ProjectManageMembersModal from "./ProjectManageMembersModal";
import ModalLayout from "../../../components/Common/ModalLayout";
import ProjectInfo from "./ProjectCreate";
import AddResources from "./AddResources";

const Project = ({ isDeveloper }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const projectsData = useSelector((state) => state.projects.projects);
  const projectsTotal = useSelector((state) => state.projects.projectsTotal);
  const usersData = useSelector((state) => state.auth.userInfoData);

  const pageLimit = 12;
  const pageCount = Math.ceil(projectsTotal / pageLimit);

  const [projectSearchValue, setProjectSearchValue] = useState("");
  const [projectListData, setProjectListData] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    overview: false,
    list: false,
    userProjects: false,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [userProjects, setUserProjects] = useState([]);

  // Modal States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [stepOne, setStepOne] = useState(true);
  const [managingMembersProject, setManagingMembersProject] = useState(null);

  // Total projects count calculation
  const totalProjectsCount = useMemo(() => {
    if (isDeveloper) return userProjects?.length || 0;
    return projectsData?.count?.totalcount ?? projectsTotal ?? projectListData?.length ?? 0;
  }, [isDeveloper, userProjects, projectsData, projectsTotal, projectListData]);

  // Overview loading
  useEffect(() => {
    setLoadingStates((prev) => ({ ...prev, overview: !projectsTotal }));
  }, [projectsTotal]);

  // Fetch paginated projects (without status filter constraint)
  const getListData = useCallback(async () => {
    setLoadingStates((prev) => ({ ...prev, list: true }));
    setCurrentPage(0);
    await dispatch.projects.listProjectData({
      limit: pageLimit,
      currentPage: 0,
      status: "all",
      search: projectSearchValue,
    });
    setLoadingStates((prev) => ({ ...prev, list: false }));
  }, [dispatch, projectSearchValue]);

  useEffect(() => {
    getListData();
  }, [getListData]);

  // Fetch user projects
  useEffect(() => {
    if (!usersData) return;

    const fetchProjects = async () => {
      try {
        setLoadingStates((prev) => ({ ...prev, userProjects: true }));
        const response = await dispatch.projects.allProjectsOfUser({
          user_id: usersData._id,
        });
        setUserProjects(response?.projects || []);
      } catch (err) {
        console.error("Error fetching user projects:", err);
      } finally {
        setLoadingStates((prev) => ({ ...prev, userProjects: false }));
      }
    };
    fetchProjects();
  }, [usersData, dispatch]);

  // Update project list data when projectsData changes
  useEffect(() => {
    if (projectsData?.data) {
      setProjectListData(projectsData.data);
    }
  }, [projectsData?.data]);

  // Handle page navigation
  const handlePageClick = useCallback(
    async ({ selected }) => {
      setLoadingStates((prev) => ({ ...prev, list: true }));
      setCurrentPage(selected);
      await dispatch.projects.listProjectData({
        limit: pageLimit,
        currentPage: selected,
        status: "all",
        search: projectSearchValue,
      });
      setLoadingStates((prev) => ({ ...prev, list: false }));
    },
    [dispatch, projectSearchValue]
  );

  // Search handler
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setProjectSearchValue(val);
    dispatch.projects.listProjectData({
      limit: pageLimit,
      currentPage: 0,
      status: "all",
      search: val,
    });
  };

  // Create Project Modal controls
  const handleOpenCreateModal = () => {
    setEditingProject(null);
    setStepOne(true);
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (proj) => {
    setEditingProject(proj);
    setStepOne(true);
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setEditingProject(null);
    getListData();
    navigate("/projects");
  };

  const nextStep = (stepComplete) => {
    if (!stepComplete) {
      setStepOne(false);
    } else {
      setStepOne(true);
    }
  };

  return (
    <div className="container mx-auto py-6 sm:py-8">
      {/* Top Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-900">Projects</h1>
            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold font-mono">
              {totalProjectsCount} total
            </span>
          </div>
          <p className="text-slate-500 text-sm">
            Manage your active projects, team members, and specifications
          </p>
        </div>

        {/* Move + Create Project button inside Project Module Header */}
        {!isDeveloper && (
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all shadow-xs shrink-0"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Create Project</span>
          </button>
        )}
      </div>


      {/* User Projects Section */}
      {isDeveloper && (
        <div className="mb-10">
          <h2 className="text-lg font-bold text-slate-900 mb-4">My Projects</h2>
          {loadingStates.userProjects ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array(4).fill(0).map((_, i) => <Skeleton key={i} height={200} />)}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {userProjects?.map((project) => (
                <ProjectCards
                  key={project.project_id || project._id}
                  project={project}
                  projectId={project.project_id || project._id}
                  onEdit={handleOpenEditModal}
                  onManageMembers={(p) => setManagingMembersProject(p)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Project Listing Section */}
      {!isDeveloper && (
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          {/* Controls Bar: Clean Search Bar without Status Filter Buttons */}
          <div className="p-6 border-b border-slate-100/90 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
            <div>
              <h2 className="text-base font-bold text-slate-900 tracking-tight">All Projects</h2>
              <p className="text-xs text-slate-400 font-medium mt-0.5">Explore active projects and assign members</p>
            </div>

            <div className="relative w-full sm:w-72">
              <svg className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                type="text"
                placeholder="Search Project"
                value={projectSearchValue}
                onChange={handleSearchChange}
                className="w-full pl-9 pr-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-slate-900 bg-white"
              />
            </div>
          </div>

          {/* Grid Listing */}
          <div className="p-6">
            {loadingStates.list ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array(8).fill(0).map((_, i) => <Skeleton key={i} height={220} />)}
              </div>
            ) : projectListData?.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-center px-4">
                <div className="w-16 h-16 rounded-2xl bg-slate-100/80 border border-slate-200/80 text-slate-400 flex items-center justify-center mb-4 shadow-2xs">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1">
                  No projects found
                </h3>
                <p className="text-xs text-slate-500 font-medium max-w-sm leading-relaxed mb-5">
                  {projectSearchValue
                    ? `We couldn't find any project matching "${projectSearchValue}". Try checking for typos or searching by client name.`
                    : "No active projects are currently listed in the system."}
                </p>
                {projectSearchValue && (
                  <button
                    onClick={() => {
                      setProjectSearchValue("");
                      dispatch.projects.listProjectData({
                        limit: pageLimit,
                        currentPage: 0,
                        status: "all",
                        search: "",
                      });
                    }}
                    className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700 transition-all flex items-center gap-2 shadow-2xs"
                  >
                    <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Clear Search</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence mode="popLayout">
                  {projectListData.map((project) => (
                    <ProjectCards
                      key={project._id}
                      project={project}
                      projectId={project._id}
                      onEdit={handleOpenEditModal}
                      onManageMembers={(p) => setManagingMembersProject(p)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}

            {projectsTotal > pageLimit && (
              <div className="mt-8">
                <Pagination
                  handlePageClick={handlePageClick}
                  pageCount={pageCount}
                  pageLimit={pageLimit}
                  currentPage={currentPage}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create / Edit Project Modal */}
      {isCreateModalOpen && (
        <ModalLayout closeModal={handleCloseCreateModal} open={isCreateModalOpen}>
          {stepOne ? (
            <ProjectInfo
              nextStep={nextStep}
              projectInfoEdit={editingProject}
              projectId={editingProject?._id || editingProject?.id}
              closeModal={handleCloseCreateModal}
            />
          ) : (
            <AddResources closeModal={handleCloseCreateModal} />
          )}
        </ModalLayout>
      )}

      {/* Manage Members Modal */}
      {managingMembersProject && (
        <ProjectManageMembersModal
          project={managingMembersProject}
          onClose={() => setManagingMembersProject(null)}
        />
      )}
    </div>
  );
};

export default Project;
